import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Chip, CircularProgress, Icon, IconButton, Switch, Tooltip } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import useAuthContext from '../../hooks/useAuthContext';
import { ReportService } from '../../hooks';
import { useConfirmation } from '../../hooks/ConfirmationService';
import { useFarmDateTime } from '../../hooks/useFarmDateTime';
import { extractApiErrorMessage, notifications } from '../../utils';
import { ScheduledReportViewModel, ReportFrequencyTranslations } from '../../models/report';
import { AnimalTrendDataTypeTranslations } from '../../models/animal';
import { RelativeDateWindowTranslations } from '../../utils/date-range';
import { DayOfWeekTranslations } from '../../models/common';
import { AnimalTrendData, toAnimalTrendDataByKey } from '../../models/animal-trend-data';
import TrendDataMulti from '../../components/TrendDataMulti/component';
import ScheduleReportModal from '../../components/ScheduleReportModal/component';
import { Skeleton } from '../../elements/ui/skeleton';

const AnimalReports: React.FC = () => {
  const { t } = useTranslation();
  const { state } = useAuthContext();
  const navigate = useNavigate();
  const confirmDialog = useConfirmation();
  const { formatDate } = useFarmDateTime();

  const reportService = new ReportService();

  // farmId is a claim only a farm token carries, so it is optional. This page sits behind a farm
  // permission and so always has one in practice; the local lets the guard before the render
  // narrow it once for the ScheduleReportModal prop rather than assert it at each use.
  const farmId = state.farmId;

  const [reports, setReports] = React.useState<ScheduledReportViewModel[]>([]);
  const [isBusy, setIsBusy] = React.useState(true);
  const [sendingId, setSendingId] = React.useState<string | null>(null);

  const [editingReport, setEditingReport] = React.useState<ScheduledReportViewModel | null>(null);

  const [expandedIds, setExpandedIds] = React.useState<Set<string>>(new Set());
  const [loadingIds, setLoadingIds] = React.useState<Set<string>>(new Set());
  const [previewByKey, setPreviewByKey] = React.useState<Record<string, Record<string, AnimalTrendData>>>({});

  // Keying by the report's own config (not just its id) means an edit that changes the
  // trend type/window/sources naturally invalidates the cache instead of showing stale data.
  const getPreviewKey = (report: ScheduledReportViewModel) =>
    [
      report.id,
      report.animalTrendDataType,
      report.relativeDateWindow,
      report.sources.map((s) => s.savedSearchId).join(',')
    ].join(':');

  const loadReports = React.useCallback(() => {
    setIsBusy(true);
    reportService
      .getAllAsync()
      .then((result) => {
        setReports(result);
        setIsBusy(false);
      })
      .catch((e) => {
        setIsBusy(false);
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      });
  }, [farmId]);

  React.useEffect(() => {
    loadReports();
  }, [loadReports]);

  const handleToggleActive = (report: ScheduledReportViewModel) => {
    reportService
      .setActiveAsync(report.id, !report.isActive)
      .then(loadReports)
      .catch((e) => notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') })));
  };

  const handleDelete = (report: ScheduledReportViewModel) => {
    confirmDialog({
      dialogId: 'delete-report-dialog',
      title: t('f-report-delete-title'),
      message: t('f-report-delete-msg', { name: report.name, interpolation: { escapeValue: false } }),
      alertSeverity: 'warning'
    }).then(() => {
      reportService
        .deleteAsync(report.id)
        .then(() => {
          notifications.success(t('f-report-delete-ok'));
          loadReports();
        })
        .catch((e: unknown) => notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') })));
    });
  };

  const handleSendNow = (report: ScheduledReportViewModel) => {
    setSendingId(report.id);
    reportService
      .sendNowAsync(report.id)
      .then(() => {
        notifications.success(t('f-report-send-now-ok'));
        loadReports();
      })
      .catch((e: unknown) => notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') })))
      .finally(() => setSendingId(null));
  };

  const handleTogglePreview = (report: ScheduledReportViewModel) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(report.id)) {
        next.delete(report.id);
      } else {
        next.add(report.id);
      }
      return next;
    });
  };

  // Fetches preview data for every currently expanded report whose cache key isn't populated yet.
  // Running off expandedIds/reports (rather than only the click handler) means editing a report
  // that's already expanded automatically triggers a re-fetch for its new config.
  React.useEffect(() => {
    reports.forEach((report) => {
      if (!expandedIds.has(report.id)) {
        return;
      }

      const key = getPreviewKey(report);
      if (previewByKey[key] || loadingIds.has(report.id)) {
        return;
      }

      setLoadingIds((prev) => new Set(prev).add(report.id));
      reportService
        .previewAsync(report.animalTrendDataType, report.relativeDateWindow, report.sources)
        .then((data) => setPreviewByKey((prev) => ({ ...prev, [key]: toAnimalTrendDataByKey(data) })))
        .catch((e) => notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') })))
        .finally(() =>
          setLoadingIds((prev) => {
            const next = new Set(prev);
            next.delete(report.id);
            return next;
          })
        );
    });
  }, [expandedIds, reports]);

  // Scheduling a report needs a farm to scope its saved searches to. Placed after every hook so
  // the hook order stays stable, and after the effects so a farmless token still settles quietly.
  if (!farmId) {
    return (
      <div className="flex flex-col gap-4 p-4">
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-sm text-gray-500">
          {t('f-report-no-reports')}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <IconButton color="primary" size="medium" aria-label={t('f-com-back')} onClick={() => navigate('/animal')}>
            <Icon>arrow_back</Icon>
          </IconButton>
          <h2 className="text-lg font-semibold text-gray-800">{t('f-report-my-reports')}</h2>
        </div>
        <ScheduleReportModal farmId={farmId} onSaved={loadReports} />
        {editingReport && (
          <ScheduleReportModal
            farmId={farmId}
            report={editingReport}
            hideTrigger
            onClose={() => setEditingReport(null)}
            onSaved={loadReports}
          />
        )}
      </div>

      {isBusy && (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      )}

      {!isBusy && reports.length === 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-sm text-gray-500">
          {t('f-report-no-reports')}
        </div>
      )}

      {!isBusy && reports.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">{t('f-report-name')}</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">{t('f-com-animal-trend-type')}</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">{t('f-report-relative-window')}</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">{t('f-report-schedule')}</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">{t('f-report-recipients')}</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">{t('f-report-next-send')}</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">{t('f-report-active')}</th>
                <th className="px-4 py-3 text-right font-semibold text-gray-700">{t('f-com-actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {reports.map((report) => (
                <React.Fragment key={report.id}>
                  <tr className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-800">{report.name}</div>
                      <div className="text-xs text-gray-500">{report.sources.map((s) => s.name).join(', ')}</div>
                    </td>
                    <td className="px-4 py-3">{t(AnimalTrendDataTypeTranslations[report.animalTrendDataType])}</td>
                    <td className="px-4 py-3">{t(RelativeDateWindowTranslations[report.relativeDateWindow])}</td>
                    <td className="px-4 py-3">
                      {t(ReportFrequencyTranslations[report.frequency])}
                      {report.frequency === 'Weekly' && ` (${t(DayOfWeekTranslations[report.dayOfWeek])})`}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1 max-w-50">
                        {report.recipients.map((email) => (
                          <Chip key={email} size="small" label={email} className="!bg-gray-100 !text-gray-700" />
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {report.nextSendAt ? formatDate(report.nextSendAt) : '-'}
                    </td>
                    <td className="px-4 py-3">
                      <Switch
                        checked={report.isActive}
                        onChange={() => handleToggleActive(report)}
                        color="primary"
                        size="small"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <Box display="flex" justifyContent="flex-end">
                        <Tooltip arrow title={t('f-report-preview')}>
                          <IconButton size="small" color="primary" onClick={() => handleTogglePreview(report)}>
                            <Icon>{expandedIds.has(report.id) ? 'expand_less' : 'visibility'}</Icon>
                          </IconButton>
                        </Tooltip>
                        <Tooltip arrow title={t('f-report-edit')}>
                          <IconButton size="small" color="primary" onClick={() => setEditingReport(report)}>
                            <Icon>{'edit'}</Icon>
                          </IconButton>
                        </Tooltip>
                        <Tooltip arrow title={t('f-report-send-now')}>
                          <span>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleSendNow(report)}
                              disabled={sendingId === report.id}
                            >
                              {sendingId === report.id ? <CircularProgress size={18} /> : <Icon>{'send'}</Icon>}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip arrow title={t('f-com-remove')}>
                          <IconButton size="small" color="primary" onClick={() => handleDelete(report)}>
                            <Icon>{'delete_outline'}</Icon>
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </td>
                  </tr>
                  {expandedIds.has(report.id) &&
                    (() => {
                      const preview = previewByKey[getPreviewKey(report)];
                      return (
                        <tr>
                          <td colSpan={8} className="px-4 py-4 bg-gray-50">
                            {loadingIds.has(report.id) && !preview ? (
                              <div className="flex justify-center py-8">
                                <CircularProgress size={48} />
                              </div>
                            ) : (
                              preview && (
                                <TrendDataMulti
                                  animalTrendDataType={report.animalTrendDataType}
                                  trendData={Object.entries(preview).map(([key, value]) => ({
                                    name: report.sources[parseInt(key, 10)]?.name ?? `Search ${key}`,
                                    color: undefined,
                                    data: value.trendData.map((trend) => ({
                                      ...trend,
                                      id: parseInt(key, 10),
                                      trendDataType: report.animalTrendDataType,
                                      color: undefined
                                    }))
                                  }))}
                                  slaughterhouseDataAverage={null}
                                  weightAverage={null}
                                  onAnimalTrendDataTypeChanged={() => {}}
                                  disableTrendTypeSelect
                                />
                              )
                            )}
                          </td>
                        </tr>
                      );
                    })()}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AnimalReports;
